import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { RefreshCw, Loader2, Check, AlertTriangle, Users } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = {
  novo: "Novo",
  em_analise: "Em análise",
  pendente_documentacao: "Pendente documentação",
  aprovado: "Aprovado",
  aprovacao_parcial: "Aprovação parcial",
  pago: "Pago",
  contratado: "Contratado",
  negado: "Negado",
};

const KIND_LABEL = {
  contrato: "Contrato Normal",
  parcelado: "Parcelado",
  acordo: "Acordo",
};

export default function RecalcStatusPage() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const loadPreview = async () => {
    setLoading(true);
    setPreview(null);
    try {
      const { data } = await api.post("/admin/client-status/preview");
      setPreview(data);
      if ((data.total_candidatos || 0) === 0) {
        toast.success("Nenhum cliente precisa de correção.");
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Falha ao carregar preview");
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    if (!preview || !preview.changes?.length) return;
    if (!window.confirm(`Confirma a atualização de ${preview.changes.length} cliente(s) para "Contratado"? Esta ação será registrada em histórico.`)) return;
    setApplying(true);
    try {
      const { data } = await api.post("/admin/client-status/apply", { confirm: true });
      toast.success(`${data.updated} cliente(s) atualizado(s) para Contratado.`);
      setPreview(null);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Falha ao aplicar correção");
    } finally {
      setApplying(false);
    }
  };

  useEffect(() => { loadPreview(); /* eslint-disable-next-line */ }, []);

  return (
    <Layout>
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]">Administração</div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1 flex items-center gap-2" data-testid="recalc-title">
          <RefreshCw size={22} className="text-[#D4AF37]"/> Recalcular status de clientes
        </h1>
        <p className="text-[#A3A3A3] text-[13px] sm:text-sm mt-1.5 max-w-2xl">
          Sincroniza o status dos clientes que possuem operações ativas (contratos, parcelados, acordos) para <span className="text-[#E8C547] font-medium">Contratado</span>.
        </p>
      </div>

      <div className="fc-card p-4 mb-4 border-[#262626]">
        <div className="flex items-start gap-3">
          <AlertTriangle size={18} className="text-amber-400 mt-0.5 shrink-0"/>
          <div className="text-[12.5px] text-[#A3A3A3] leading-relaxed">
            <div className="text-[#E5E5E5] font-medium mb-1">Regra aplicada</div>
            Sempre que um cliente tiver pelo menos uma operação <strong>ATIVA</strong> (contrato normal, parcelado ou acordo),
            seu status é atualizado para <strong className="text-[#E8C547]">Contratado</strong>. Clientes já como Contratado
            ou Pago sem operação ativa não são alterados. A operação é segura, idempotente e cria registros em
            <code className="text-[11px] bg-[#0a0a0a] px-1 py-0.5 rounded ml-1">status_history</code>.
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-5">
        <button
          onClick={loadPreview}
          disabled={loading}
          data-testid="recalc-preview-btn"
          className="px-4 py-2 rounded-md bg-[#141414] border border-[#262626] hover:border-[#D4AF37] text-[13px] flex items-center gap-2 disabled:opacity-50"
        >
          {loading ? <Loader2 size={14} className="animate-spin"/> : <RefreshCw size={14}/>}
          Recarregar preview
        </button>
        <button
          onClick={apply}
          disabled={applying || !preview?.changes?.length}
          data-testid="recalc-apply-btn"
          className="px-4 py-2 rounded-md bg-[#D4AF37] text-black font-medium text-[13px] flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {applying ? <Loader2 size={14} className="animate-spin"/> : <Check size={14}/>}
          Aplicar correção {preview?.changes?.length ? `(${preview.changes.length})` : ""}
        </button>
      </div>

      {loading && (
        <div className="fc-card p-8 flex items-center justify-center gap-2 text-[#A3A3A3]">
          <Loader2 className="animate-spin" size={16}/> Verificando clientes...
        </div>
      )}

      {!loading && preview && (
        <div className="fc-card p-0 overflow-hidden" data-testid="recalc-preview-table">
          <div className="px-4 py-3 border-b border-[#1f1f1f] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users size={15} className="text-[#A3A3A3]"/>
              <span className="text-[13px]">
                <span className="font-mono-tab text-[#E5E5E5]">{preview.total_candidatos || 0}</span>
                <span className="text-[#A3A3A3] ml-1.5">cliente(s) serão atualizados para</span>
                <span className="text-[#E8C547] font-medium ml-1.5">Contratado</span>
              </span>
            </div>
          </div>

          {preview.changes?.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead className="bg-[#0f0f0f] text-[#737373] uppercase text-[10.5px] tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Cliente</th>
                    <th className="text-left px-4 py-2 font-medium">CPF</th>
                    <th className="text-left px-4 py-2 font-medium">Status atual</th>
                    <th className="text-left px-4 py-2 font-medium">Novo status</th>
                    <th className="text-left px-4 py-2 font-medium">Operações ativas</th>
                    <th className="text-left px-4 py-2 font-medium">Motivo</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.changes.map(c => (
                    <tr key={c.client_id} className="border-t border-[#1a1a1a] hover:bg-[#0e0e0e]">
                      <td className="px-4 py-2.5 text-[#E5E5E5]">{c.full_name || "—"}</td>
                      <td className="px-4 py-2.5 text-[#A3A3A3] font-mono-tab text-[12px]">{c.cpf || "—"}</td>
                      <td className="px-4 py-2.5">
                        <span className="px-2 py-0.5 rounded text-[11px] bg-[#1a1a1a] border border-[#2a2a2a] text-[#A3A3A3]">
                          {STATUS_LABEL[c.current_status] || c.current_status}
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="px-2 py-0.5 rounded text-[11px] bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#E8C547]">
                          Contratado
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex flex-wrap gap-1">
                          {(c.active_operations || []).map((op, idx) => (
                            <span key={idx} className="px-1.5 py-0.5 rounded text-[10.5px] bg-emerald-500/10 border border-emerald-500/30 text-emerald-300">
                              {KIND_LABEL[op.kind] || op.kind}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-[#A3A3A3] text-[12px]">{c.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-4 py-10 text-center text-[#737373] text-[13px]">
              Tudo certo — nenhum cliente precisa de ajuste.
            </div>
          )}
        </div>
      )}
    </Layout>
  );
}
